import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import {
    AlertsHeaderActionService,
    AlertsTimeRangeService,
    ApiService,
    hasNetworkExtension,
    INFRASTRUCTURE_URLS,
    NETWORK_EXTENSIONS,
    URLS,
} from '@netfoundry-ui/shared/services';
import { TimeRangePillComponent } from '@netfoundry-ui/ui/time-range-pill';
import { Subscription } from 'rxjs';
import { OpenZitiConsoleLibModule } from 'ziti-console-lib';

export type NetworkPageHeaderGroup = 'settings' | 'alerts' | 'operations';

interface HeaderTab {
    label: string;
    url: string;
    hidden: boolean;
}

/** Title, blurb and icon of each page group; the tabs are built in ngOnInit because they read live permissions. */
const GROUPS: Record<NetworkPageHeaderGroup, { title: string; subtitle: string; icon: string }> = {
    settings: { title: 'Settings', subtitle: 'Manage the settings of this network.', icon: 'icon-OrgSettings' },
    alerts: { title: 'Alerts', subtitle: 'What is wrong with this network now, and who is told about it.', icon: 'icon-Bell' },
    operations: { title: 'Operations', subtitle: 'Manage, upgrade and back up this network.', icon: 'icon-StatusGear' },
};

/** The list header of the network Settings, Alerts and Operations pages. */
@Component({
    selector: 'app-network-page-header',
    standalone: true,
    imports: [CommonModule, OpenZitiConsoleLibModule, TimeRangePillComponent],
    template: `
        @if (onGroupPage) {
            <lib-list-table-header [title]="title" [subtitle]="info.subtitle" [tabs]="tabs" [showAdd]="showAdd" [createLabel]="createLabel" [blockDelete]="true" (actionClicked)="onAction($event)">
                <span header-icon [class]="info.icon" aria-hidden="true"></span>
                @if (showTimeRange) {
                    <div header-action class="nf-page-header-actions">
                        @if (alertsHeaderAction.action; as action) {
                            <button
                                type="button"
                                class="lth-secondary"
                                [id]="action.id"
                                [disabled]="action.isDisabled()"
                                [title]="action.title"
                                (click)="action.run()"
                            >
                                <i [class]="action.iconClass" aria-hidden="true"></i>
                                {{ action.label }}
                            </button>
                        }
                        <app-time-range-pill
                            [selected]="alertsTimeRange.range"
                            [ranges]="alertsTimeRange.options"
                            (rangeChange)="alertsTimeRange.set($event)"
                        />
                    </div>
                }
            </lib-list-table-header>
        }
    `,
    styles: [
        ':host{display:block;margin-bottom:1rem}:host(.nf-page-header--flush){margin-bottom:0}.nf-page-header-actions{display:flex;align-items:center;gap:.5rem}',
        ':host ::ng-deep .lth-subtitle{color:var(--text-secondary)}',
    ],
})
export class NetworkPageHeaderComponent implements OnInit, OnDestroy {
    @Input() group: NetworkPageHeaderGroup = 'settings';
    /** Drops the gap under the header, for a body that already spaces itself. */
    @Input() @HostBinding('class.nf-page-header--flush') flush = false;
    @Input() showAdd = false;
    @Input() createLabel = 'Create';
    @Output() create = new EventEmitter<void>();

    tabs: HeaderTab[] = [];
    private aiAssistantEnabled = false;
    private subscription = new Subscription();

    constructor(
        public alertsTimeRange: AlertsTimeRangeService,
        public alertsHeaderAction: AlertsHeaderActionService,
        private authorizationService: AuthorizationService,
        private apiService: ApiService,
        private router: Router
    ) {}

    get info() {
        return GROUPS[this.group];
    }

    /** The title follows the selected tab; the group title is the fallback off the tab routes. */
    get title(): string {
        const path = this.router.url.split(/[?;#]/)[0];
        return this.tabs.find((tab) => tab.url === path)?.label ?? this.info.title;
    }

    onAction(action: string) {
        if (action === 'add') {
            this.create.emit();
        }
    }

    /** SupportOperationsComponent also serves the support app's own route, which these tabs do not belong to. */
    get onGroupPage(): boolean {
        const path = this.router.url.split(/[?;#]/)[0];
        return this.tabs.some((tab) => tab.url === path);
    }

    /** The window and the page's action scope the Alerts listing only, not its sibling tabs. */
    get showTimeRange(): boolean {
        return this.group === 'alerts' && this.router.url.split(/[?;#]/)[0] === URLS.ALERTS;
    }

    ngOnInit() {
        this.tabs = this.buildTabs();
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network) => {
                this.aiAssistantEnabled = hasNetworkExtension(network, NETWORK_EXTENSIONS.AI_ASSISTANT);
            })
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private buildTabs(): HeaderTab[] {
        const auth = this.authorizationService;
        const aiEnabled = () => this.aiAssistantEnabled;
        switch (this.group) {
            case 'alerts':
                return [
                    { label: 'Alerts', url: URLS.ALERTS, hidden: false },
                    {
                        label: 'Mutes',
                        url: URLS.MUTES,
                        get hidden() {
                            return !auth.canListMutes();
                        },
                    },
                    {
                        label: 'Latency Policies',
                        url: URLS.LATENCY_POLICIES,
                        get hidden() {
                            return !auth.canListLatencyPolicies();
                        },
                    },
                    {
                        label: 'Offline Policies',
                        url: URLS.OFFLINE_POLICIES,
                        get hidden() {
                            return !auth.canListOfflinePolicies();
                        },
                    },
                ];
            case 'operations':
                return [
                    { label: 'Management', url: INFRASTRUCTURE_URLS.OPERATIONS, hidden: false },
                    { label: 'Upgrades', url: INFRASTRUCTURE_URLS.OPERATIONS_UPGRADES, hidden: false },
                    { label: 'Backups', url: INFRASTRUCTURE_URLS.OPERATIONS_BACKUPS, hidden: false },
                    { label: 'Support Info', url: INFRASTRUCTURE_URLS.OPERATIONS_SUPPORT_INFO, hidden: false },
                ];
            default:
                return [
                    { label: 'Settings', url: INFRASTRUCTURE_URLS.SETTINGS, hidden: false },
                    { label: 'Firewall Info', url: INFRASTRUCTURE_URLS.SETTINGS_FIREWALL_INFO, hidden: false },
                    {
                        label: 'AI Assistant',
                        url: INFRASTRUCTURE_URLS.SETTINGS_AI_ASSISTANT,
                        get hidden() {
                            return !aiEnabled();
                        },
                    },
                ];
        }
    }
}
